/**
 * Integrytas Group - Email & Communication Templates (ECM)
 * Service: EcmService  (BASE SERVICE)
 *
 * Renders a master communication template for a record and delivers it on the
 * chosen channel. Reuses the DOC merge engine (tokens + {{#section}} bindings),
 * fetching the record + related rows UNDER THE CALLER'S JWT (RLS respected).
 *   - INAPP   delivers synchronously (csm_notification via ecm_post_inapp)
 *   - EMAIL / SMS / WEBHOOK enqueue an ecm_message (QUEUED) for the worker
 */

import { apiDb } from '../../lib/api_client';
import { renderTemplate } from '../doc/DocService';
import type { AuthSession } from '../../types/auth';

type Channel = 'EMAIL' | 'INAPP' | 'SMS' | 'WEBHOOK';
interface EcmTemplate { ecm_template_id: number; internal_code: string; channel: Channel; primary_entity: string | null; pk_column: string | null }
interface EcmBinding { binding_name: string; related_entity: string; fk_column: string; order_by: string | null }
type Row = Record<string, unknown>;

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

export interface EcmSendResult { channel: Channel; subject: string; body_html: string; status: 'SENT' | 'QUEUED'; messageId: number | null }

/**
 * @param recipients  for INAPP: user ids; for EMAIL/SMS/WEBHOOK: addresses/url.
 */
export async function sendForRecord(
  templateCode: string,
  recordId: string,
  opts: { session: AuthSession; recipients: (string | number)[]; channel?: Channel; locale?: string },
): Promise<EcmSendResult> {
  const userId = opts.session.effective_user.user_id;

  const { data: tpl, error: e1 } = await apiDb.from('ecm_template')
    .select('ecm_template_id, internal_code, channel, primary_entity, pk_column')
    .eq('internal_code', templateCode).single<EcmTemplate>();
  if (e1 || !tpl) throw new Error(e1?.message ?? 'ECM.TEMPLATE_NOT_FOUND');

  const locale = opts.locale ?? opts.session.effective_user.preferred_locale ?? 'EN_US';
  const { data: tlRows } = await apiDb.from('ecm_template_tl')
    .select('locale_code, subject, body_html, body_text').eq('ecm_template_id', tpl.ecm_template_id);
  const tls = (tlRows ?? []) as { locale_code: string; subject: string | null; body_html: string | null; body_text: string | null }[];
  const tl = tls.find(t => t.locale_code === locale) ?? tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
  if (!tl) throw new Error('ECM.NO_CONTENT');

  // Data — under the caller's JWT (RLS).
  let primary: Row = {};
  const bindingData: Record<string, Row[]> = {};
  if (tpl.primary_entity && tpl.pk_column) {
    const { data: p, error: e2 } = await apiDb.from(tpl.primary_entity).select('*').eq(tpl.pk_column, recordId).maybeSingle();
    if (e2) throw new Error(e2.message);
    if (!p) throw new Error('ECM.RECORD_NOT_FOUND_OR_FORBIDDEN');
    primary = p as Row;
    const { data: bRows } = await apiDb.from('ecm_binding')
      .select('binding_name, related_entity, fk_column, order_by').eq('ecm_template_id', tpl.ecm_template_id).eq('is_active', 'Y');
    for (const b of (bRows ?? []) as EcmBinding[]) {
      let q = apiDb.from(b.related_entity).select('*').eq(b.fk_column, recordId);
      if (b.order_by) q = q.order(b.order_by, { ascending: true });
      const { data: rows } = await q;
      bindingData[b.binding_name] = (rows ?? []) as Row[];
    }
  }

  const subject = renderTemplate(tl.subject ?? '', primary, {});
  const bodyHtml = renderTemplate(tl.body_html ?? '', primary, bindingData);
  const bodyText = tl.body_text ? renderTemplate(tl.body_text, primary, bindingData) : stripHtml(bodyHtml);
  const channel: Channel = opts.channel ?? tpl.channel;

  if (channel === 'INAPP') {
    await apiDb.rpc('ecm_post_inapp', { p_user_ids: opts.recipients.map(Number), p_title: subject, p_body: bodyText });
  }

  // Always log (in-app as SENT; others QUEUED for the worker).
  const status: 'SENT' | 'QUEUED' = channel === 'INAPP' ? 'SENT' : 'QUEUED';
  let messageId: number | null = null;
  try {
    const { data: msg } = await apiDb.from('ecm_message').insert({
      ecm_template_id: tpl.ecm_template_id, channel, entity_name: tpl.primary_entity, record_id: recordId,
      locale_code: locale, recipients: opts.recipients, subject, body_html: bodyHtml, status,
      sent_at: status === 'SENT' ? new Date().toISOString() : null, requested_by_user_id: userId,
    }).select('ecm_message_id').single();
    messageId = (msg as { ecm_message_id: number } | null)?.ecm_message_id ?? null;
  } catch { /* logging optional */ }

  return { channel, subject, body_html: bodyHtml, status, messageId };
}

export const EcmService = { sendForRecord, renderTemplate };
